// src/screens/sales/store/components/form/hooks/useForm.ts (веб)

import React, { SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  StoreInterface,
  StoreDeliveryZone,
  SettingsFormItem,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { getApi } from '../../../../../../api/api'
import { useFiles } from '../../../../../../common/hooks/useFiles'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { minUploadFilesCount, storeValidations } from '../../../../../../common/utils/constants'
import { useInput } from '../../../../../../common/hooks/useInput'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'
import { StoreFormView } from '../../../../../../common/types/enums'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentCluster,
  setCurrentCluster,
} from '../../../../../../store/slices/currentClusterSlice'

const validations: ValidationRulesInterface[] = storeValidations

export const useForm = (
  data: StoreInterface,
  setData: React.Dispatch<SetStateAction<StoreInterface>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  el?: StoreInterface,
) => {
  const [salesApi] = useState(getSalesApi())
  const [api] = useState(getApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[] | []>([])
  const [disabled, setDisabled] = useState<boolean>(true)
  const { files, setFiles, pairs, setPairs, dataInit, fetchFiles } = useFiles()
  const [view, setView] = useState<StoreFormView>(StoreFormView.MAIN)
  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )
  const { inputHandler } = useInput(setData)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()
  const dispatch = useDispatch()
  const currentCluster = useSelector(selectCurrentCluster)

  const [mapModalActive, setMapModalActive] = useState(false)
  const [editingDeliveryId, setEditingDeliveryId] = useState<number | null>(null)

  // ❌ Свежие тарифы с бекенда
  const [deliveryTariffs, setDeliveryTariffs] = useState<SettingsFormItem[]>([])

  // ❌ Загружаем свежий кластер при открытии формы
  useEffect(() => {
    const fetchCluster = async () => {
      if (!currentCluster?.id) return
      try {
        const fresh = await api.getCluster(currentCluster.id)
        setDeliveryTariffs(fresh.delivery || [])
        dispatch(setCurrentCluster(fresh))
      } catch (error) {
        console.error('Failed to refresh cluster:', error)
        setDeliveryTariffs(currentCluster?.delivery || [])
      }
    }

    fetchCluster()
  }, [currentCluster?.id, api, dispatch])

  useEffect(() => {
    if (el && el.files && el.files?.length > 0) {
      fetchFiles(el.files)
      el.files = []
    }
    if (el) setData(el)
  }, [el])

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(1000)

      const { createdAt, updatedAt, c1_id, moysklad_id, ...rest } = data

      // Чистим deliveryZones от лишних полей
      const cleanedDeliveryZones = (rest.deliveryZones || []).map(
        ({ id, storeId, clusterId, createdAt, updatedAt, ...zone }: any) => zone,
      )

      const payload: any = {
        ...rest,
        deliveryZones: cleanedDeliveryZones,
      }

      if (data.id) {
        await salesApi.updateStore({
          ...payload,
          file: dataInit(files),
        })
      } else {
        await salesApi.createStore({
          ...payload,
          file: dataInit(files),
        })
      }
      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
    }
  }, [salesApi, data, files, dataInit, getList, setModalActive, showCommonLoader, hideCommonLoader])

  const gMapInputHandler = useCallback(
    (inputData: StoreInterface) => {
      setData(inputData)
    },
    [setData],
  )

  const toggleAction = useCallback(
    (fieldName: string) => {
      setData((prev) => {
        return { ...prev, [fieldName]: !prev[fieldName] }
      })
    },
    [setData],
  )

  const openZoneEditor = useCallback((deliveryId: number) => {
    setEditingDeliveryId(deliveryId)
    setMapModalActive(true)
  }, [])

  const saveZone = useCallback(
    (polygon: Array<{ lat: number; lng: number }>) => {
      if (!editingDeliveryId) return
      setData((prev) => {
        const existing = (prev.deliveryZones || []).filter(
          (z: StoreDeliveryZone) => z.clusterDeliveryId !== editingDeliveryId,
        )
        return {
          ...prev,
          deliveryZones: [...existing, { clusterDeliveryId: editingDeliveryId, polygon }],
        }
      })
      setMapModalActive(false)
      setEditingDeliveryId(null)
    },
    [editingDeliveryId, setData],
  )

  const removeZone = useCallback(
    (deliveryId: number) => {
      setData((prev) => ({
        ...prev,
        deliveryZones: (prev.deliveryZones || []).filter(
          (z: StoreDeliveryZone) => z.clusterDeliveryId !== deliveryId,
        ),
      }))
    },
    [setData],
  )

  const getZoneByDeliveryId = useCallback(
    (deliveryId: number): StoreDeliveryZone | undefined => {
      return data.deliveryZones?.find((z: StoreDeliveryZone) => z.clusterDeliveryId === deliveryId)
    },
    [data.deliveryZones],
  )

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item])
      }
    }
  }, [data])

  useEffect(() => {
    setDisabled(
      Boolean(
        files.length < minUploadFilesCount ||
        !data.address.latitude ||
        validationResult.some((el) => {
          if (el.name === 'deliveryRadius' && !data.withDelivery) return false
          return el.notValid
        }),
      ),
    )
  }, [files, validationResult, data.address.latitude, data.withDelivery])

  return {
    formAction,
    data,
    inputHandler,
    setDirty,
    getValidationResult,
    files,
    pairs,
    setFiles,
    setPairs,
    disabled,
    gMapInputHandler,
    toggleAction,
    view,
    setView,
    deliveryTariffs,
    mapModalActive,
    setMapModalActive,
    editingDeliveryId,
    openZoneEditor,
    saveZone,
    removeZone,
    getZoneByDeliveryId,
  }
}
